import { useEffect, useMemo, useState } from 'react';

import { useAssignAudioBlockToNewSpeaker } from '@/api/mutations/useAssignAudioBlockToNewSpeaker';
import { useAssignAudioSpeaker } from '@/api/mutations/useAssignAudioSpeaker';
import { useRenameMatterSpeaker } from '@/api/mutations/useRenameMatterSpeaker';
import { useGetMatterSpeakers } from '@/api/queries/useGetAudioSpeakers';
import { useGetDocumentAudioBlocks, useGetDocumentAudioUrl } from '@/api/queries/useGetDocumentAudio';
import type { AudioBlocks, MatterSpeakers } from '@/api/types/backend-dto-types';
import { AudioPlayer } from '@/new/components/doc-viewer/audio/audio-player';
import { AudioTranscript } from '@/new/components/doc-viewer/audio/audio-transcript';
import { useAudioPlayback } from '@/new/components/doc-viewer/audio/use-audio-playback';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/new/components/shadcn/select';
import { cn } from '@/new/lib/utils';
import { TimeRangeWithId } from '@/types';

const NO_TIME_HIGHLIGHTS: TimeRangeWithId[] = [];

const NEW_SPEAKER_SENTINEL = '__new-speaker__';

/** A diarized voice, with the first block it speaks in as its listening sample */
interface DiarizedVoice {
  id: string;
  diarizationLabel: number;
  blockCount: number;
  firstBlock: AudioBlocks[number];
  speaker: { id: string; name: string } | null;
}

function deriveVoices(blocks: AudioBlocks | undefined): DiarizedVoice[] {
  const byId = new Map<string, DiarizedVoice>();
  for (const block of blocks ?? []) {
    const fileSpeaker = block.audioFileSpeaker;

    const voice = byId.get(fileSpeaker.id);
    if (!voice) {
      byId.set(fileSpeaker.id, {
        id: fileSpeaker.id,
        diarizationLabel: fileSpeaker.diarizationLabel,
        blockCount: 1,
        firstBlock: block,
        speaker: fileSpeaker.speaker,
      });
    } else {
      voice.blockCount += 1;
      if (block.startTimeSeconds < voice.firstBlock.startTimeSeconds) voice.firstBlock = block;
    }
  }
  // Unidentified voices first — they are what the panel is for
  return [...byId.values()].sort(
    (a, b) => Number(a.speaker !== null) - Number(b.speaker !== null) || a.diarizationLabel - b.diarizationLabel,
  );
}

/** The row's dropdown slot swapped for a text input: naming a new speaker, or renaming an existing one. */
type NameDraft = { mode: 'new' } | { mode: 'rename'; speaker: MatterSpeakers[number] };

interface VoiceRowProps {
  voice: DiarizedVoice;
  speakers: MatterSpeakers;
  isAssigning: boolean;
  isRenaming: boolean;
  isLocked: boolean;
  onAssign: (assignment: { speakerId: string } | { name: string }) => void;
  onRename: (params: { speakerId: string; name: string }) => void;
  onFindInTranscript: () => void;
}

function VoiceRow({
  voice,
  speakers,
  isAssigning,
  isRenaming,
  isLocked,
  onAssign,
  onRename,
  onFindInTranscript,
}: VoiceRowProps) {
  const [draft, setDraft] = useState<NameDraft | null>(null);
  const [draftName, setDraftName] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const startDraft = (next: NameDraft) => {
    setIsPickerOpen(false);
    setDraft(next);
    setDraftName(next.mode === 'rename' ? next.speaker.name : '');
  };

  const clearDraft = () => {
    setDraft(null);
    setDraftName('');
  };

  const commitDraft = () => {
    const name = draftName.trim();
    if (draft === null || name === '') {
      clearDraft();
      return;
    }

    if (draft.mode === 'new') onAssign({ name });
    else if (name !== draft.speaker.name) onRename({ speakerId: draft.speaker.id, name });

    clearDraft();
  };

  const isUnassigned = voice.speaker === null;

  return (
    <li className="flex flex-col gap-1 py-2 @md/voices:py-1.25">
      <div className="flex flex-col gap-1 @md/voices:flex-row @md/voices:items-center @md/voices:gap-3">
        <div className="flex items-center justify-between gap-2 @md/voices:contents">
          <p
            className={cn(
              'ml-1.5 flex min-w-0 flex-1 items-center gap-2 truncate text-xs font-medium',
              isUnassigned ? 'text-amber-600' : 'text-gray-500',
            )}
          >
            {isUnassigned && <span className="size-1.25 shrink-0 rounded-full bg-amber-400" />}
            Speaker {voice.diarizationLabel}
          </p>

          <Button
            size="sm"
            variant="ghost"
            onClick={onFindInTranscript}
            className="shrink-0"
            title={`Jump to where speaker ${voice.diarizationLabel} first talks in the transcript`}
          >
            <Icon className="lucide--text-search" />
            Show in transcript
          </Button>
        </div>

        <div className="min-w-0 flex-1 @md/voices:w-52 @md/voices:flex-none">
          {isAssigning || isRenaming ? (
            <p className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.25 text-[13px] text-gray-500">
              <Icon className="lucide--loader animate-spin ease-in-out" />
              {isRenaming ? 'Renaming…' : 'Assigning…'}
            </p>
          ) : draft !== null ? (
            <Input
              autoFocus
              disabled={isLocked}
              className="h-auto px-2.5 py-1.25 text-[13px] text-gray-800"
              placeholder="Speaker name"
              aria-label={draft.mode === 'rename' ? `Rename ${draft.speaker.name}` : 'New speaker name'}
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') commitDraft();
                if (event.key === 'Escape') clearDraft();
              }}
              onBlur={commitDraft}
            />
          ) : (
            <Select
              disabled={isLocked}
              open={isPickerOpen}
              onOpenChange={setIsPickerOpen}
              value={voice.speaker?.id ?? ''}
              onValueChange={(value) => {
                if (value === NEW_SPEAKER_SENTINEL) startDraft({ mode: 'new' });
                else if (value !== voice.speaker?.id) onAssign({ speakerId: value });
              }}
            >
              <SelectTrigger
                className={cn(
                  'h-auto w-full px-2.5 py-1.25 text-[13px] text-gray-800',
                  isUnassigned &&
                    'border-amber-200 bg-amber-50 text-amber-600 data-[placeholder]:text-amber-600 [&_svg]:text-amber-500',
                )}
                aria-label={`Assign speaker ${voice.diarizationLabel}`}
              >
                <SelectValue placeholder="Needs assignment" />
              </SelectTrigger>
              <SelectContent>
                {speakers.map((speaker) => (
                  <SelectItem key={speaker.id} value={speaker.id} className="group/speaker [&>span:last-child]:flex-1">
                    <span className="flex w-full items-center justify-between gap-1.5">
                      {speaker.name}
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={`Rename ${speaker.name}`}
                        title="Rename this speaker everywhere in this matter"
                        className="rounded p-0.5 text-gray-400 opacity-0 group-hover/speaker:opacity-100 hover:text-gray-700"
                        onPointerUp={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          startDraft({ mode: 'rename', speaker });
                        }}
                      >
                        <Icon className="lucide--pencil size-3.5" />
                      </button>
                    </span>
                  </SelectItem>
                ))}
                {speakers.length > 0 && <SelectSeparator />}
                <SelectItem value={NEW_SPEAKER_SENTINEL}>
                  <span className="flex items-center gap-1.5">
                    <Icon className="lucide--plus" /> New speaker…
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    </li>
  );
}

interface AudioSpeakerTaggingPanelProps {
  matterId: string;
  docId: string;
}

export function AudioSpeakerTaggingPanel({ matterId, docId }: AudioSpeakerTaggingPanelProps) {
  const {
    data: audioUrl,
    isLoading: isAudioUrlLoading,
    refetch: refetchAudioUrl,
  } = useGetDocumentAudioUrl(matterId, docId);
  const { data: blocks, isLoading: areBlocksLoading } = useGetDocumentAudioBlocks(matterId, docId);
  const { data: speakers } = useGetMatterSpeakers(matterId);

  const playback = useAudioPlayback({ audioUrl, refetchAudioUrl: () => void refetchAudioUrl() });

  const voices = useMemo(() => deriveVoices(blocks), [blocks]);

  const { mutate: assignSpeaker, isLoading: isAssigning } = useAssignAudioSpeaker(matterId);
  const { mutate: assignBlockToNewSpeaker, isLoading: isAssigningBlock } = useAssignAudioBlockToNewSpeaker(
    matterId,
    docId,
  );
  const { mutate: renameSpeaker, isLoading: isRenaming } = useRenameMatterSpeaker(matterId);

  const [pendingVoiceId, setPendingVoiceId] = useState<string | null>(null);
  const [pendingBlockId, setPendingBlockId] = useState<string | null>(null);
  const isWriting = isAssigning || isRenaming || isAssigningBlock;
  const [previewedBlockId, setPreviewedBlockId] = useState<string | null>(null);
  const [stopAtSeconds, setStopAtSeconds] = useState<number | null>(null);
  const { currentTime, pause } = playback;

  useEffect(() => {
    if (stopAtSeconds === null || currentTime < stopAtSeconds) return;
    setStopAtSeconds(null);
    pause();
  }, [stopAtSeconds, currentTime, pause]);

  const findInTranscript = (voice: DiarizedVoice) => {
    const { id, startTimeSeconds, durationSeconds } = voice.firstBlock;

    setPreviewedBlockId(id);
    setStopAtSeconds(startTimeSeconds + durationSeconds);
    playback.seekTo(startTimeSeconds);
    if (!playback.isPlaying) void playback.togglePlayback();
  };

  return (
    <div className="@container/panel flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col @3xl/panel:flex-row">
        <section className="flex shrink-0 flex-col border-b border-gray-200 @3xl/panel:min-h-0 @3xl/panel:w-78 @3xl/panel:border-r @3xl/panel:border-b-0">
          <div className="@container/voices max-h-58 overflow-y-auto px-5 py-2 @3xl/panel:max-h-none @3xl/panel:min-h-0 @3xl/panel:flex-1">
            {areBlocksLoading ? (
              <p className="pt-2 text-sm text-gray-400">Loading speakers…</p>
            ) : voices.length === 0 ? (
              <p className="pt-2 text-sm text-gray-600">No speakers were detected in this file.</p>
            ) : (
              <ul className="flex flex-col">
                {voices.map((voice) => (
                  <VoiceRow
                    key={voice.id}
                    voice={voice}
                    speakers={speakers ?? []}
                    isAssigning={isAssigning && pendingVoiceId === voice.id}
                    isRenaming={isRenaming && pendingVoiceId === voice.id}
                    isLocked={isWriting && pendingVoiceId !== voice.id}
                    onFindInTranscript={() => findInTranscript(voice)}
                    onAssign={(assignment) => {
                      setPendingVoiceId(voice.id);
                      assignSpeaker({ audioFileSpeakerId: voice.id, ...assignment });
                    }}
                    onRename={(params) => {
                      setPendingVoiceId(voice.id);
                      renameSpeaker(params);
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
        </section>

        <div className="flex min-h-0 flex-1 flex-col">
          <AudioTranscript
            blocks={blocks}
            isLoading={areBlocksLoading}
            currentTime={playback.currentTime}
            isPlaying={playback.isPlaying}
            seekedTo={playback.seekedTo}
            onSeek={playback.seekTo}
            timeHighlights={NO_TIME_HIGHLIGHTS}
            highlightedBlockId={previewedBlockId}
            assigningBlockId={isAssigningBlock ? pendingBlockId : null}
            isAssigningBlock={isAssigningBlock}
            onAssignBlockToNewSpeaker={(blockId) => {
              setPendingBlockId(blockId);
              assignBlockToNewSpeaker(blockId);
            }}
          />
        </div>
      </div>

      <AudioPlayer matterId={matterId} docId={docId} isAudioUrlLoading={isAudioUrlLoading} playback={playback} />
    </div>
  );
}
